import { useEffect, useReducer } from 'react'

import { ButtonDefault, ButtonRoot, ButtonText } from '@/components/common/Button'
import { FloatingHover } from '@/components/common/Floating'
import { InputSelect } from '@/components/common/Input'
import { Icon } from '@/components/icons/Icon'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import {
  CARD_FILTER_LABELS,
  CARD_FILTER_SLUGS,
  type CardFilterPartialType,
  type CardFilterSlugType,
  type CardFilterType,
  isCardFilterBooleanType,
  isCardFilterType,
  withCardFilterDefaults,
} from '@/defs/cardFilters'
import { type DropdownOptionGroupType } from '@/defs/input'
import { useClickaway } from '@/hooks/useClickaway'
import { galleryActions } from '@/store/gallery'
import { useReduxDispatch, useReduxSelector } from '@/store/hooks'
import { getClickawayIgnoreProps } from '@/utils/clickawayUtil'
import { cn } from '@/utils/commonUtil'
import { cleanGalleryFilters } from '@/utils/galleryUtil'
import { scrollToAnchor } from '@/utils/scrollUtil'

import { GalleryControlsLock } from '../GalleryControls/GalleryControlsLock/GalleryControlsLock'

import styles from './GalleryCardFilters.module.scss'
import { GalleryCardFiltersForm } from './GalleryCardFiltersForm/GalleryCardFiltersForm'
import { GalleryCardFiltersItem } from './GalleryCardFiltersItem/GalleryCardFiltersItem'

export type CardFilterFormType = (CardFilterType & { isEditing: false }) | (CardFilterPartialType & { isEditing: true })

type StateType = {
  cardFiltersDraft: CardFilterFormType[]
  isEditMode: boolean
}

type ActionType =
  | {
      action: 'close'
    }
  | {
      action: 'add'
      slug: CardFilterSlugType
    }
  | {
      action: 'edit' | 'delete'
      index: number
    }
  | {
      action: 'updateFilter'
      filter: CardFilterPartialType
      index: number
    }
  | {
      action: 'submitFilter'
      filter: CardFilterType
      index: number
    }
  | {
      action: 'sync'
      cardFilters: CardFilterType[]
    }

const INITIAL_STATE: StateType = {
  cardFiltersDraft: [],
  isEditMode: false,
}

const reducer = (state: StateType, action: ActionType): StateType => {
  const cardFiltersDraft = [...state.cardFiltersDraft]
  switch (action.action) {
    case 'add': {
      const filter = withCardFilterDefaults(action.slug)
      const filterNew: CardFilterFormType = isCardFilterBooleanType(filter)
        ? { ...filter, isEditing: false }
        : { ...filter, isEditing: true }
      return {
        cardFiltersDraft: [...cardFiltersDraft, filterNew],
        isEditMode: true,
      }
    }
    case 'edit': {
      const filter = cardFiltersDraft[action.index]
      if (filter) cardFiltersDraft[action.index] = { ...filter, isEditing: true }
      return {
        cardFiltersDraft,
        isEditMode: true,
      }
    }
    case 'delete': {
      return {
        cardFiltersDraft: cardFiltersDraft.filter((_, c) => c !== action.index),
        isEditMode: true,
      }
    }
    case 'updateFilter': {
      if (cardFiltersDraft[action.index]) cardFiltersDraft[action.index] = { ...action.filter, isEditing: true }
      return {
        cardFiltersDraft,
        isEditMode: true,
      }
    }
    case 'submitFilter': {
      if (cardFiltersDraft[action.index]) cardFiltersDraft[action.index] = { ...action.filter, isEditing: false }
      return {
        cardFiltersDraft,
        isEditMode: true,
      }
    }
    case 'sync': {
      return {
        cardFiltersDraft: action.cardFilters.map((f) => ({ ...f, isEditing: false })),
        isEditMode: false,
      }
    }
    case 'close':
    default: {
      return { ...INITIAL_STATE }
    }
  }
}
export const optionGroups: DropdownOptionGroupType<CardFilterSlugType>[] = [
  {
    options: CARD_FILTER_SLUGS.map((s) => ({
      label: <IconWithLabel iconBefore={CARD_FILTER_LABELS[s].icon}>{CARD_FILTER_LABELS[s].label}</IconWithLabel>,
      value: s,
    })),
  },
]

type Props = {
  onClickClose?: () => void
  variant?: 'desktop'
}

const GalleryCardFiltersClose = ({ onClickClose }: Pick<Props, 'onClickClose'>) => (
  <span className={styles.closeIcon}>
    <ButtonText aria-label='Close' onClick={onClickClose}>
      <Icon name='xMark' />
    </ButtonText>
  </span>
)

export const GalleryCardFilters = ({ onClickClose, variant }: Props) => {
  const cardFilters = useReduxSelector((s) => s.gallery.cardFilters)
  const dispatch = useReduxDispatch()
  const [{ cardFiltersDraft, isEditMode }, dispatchDraft] = useReducer(reducer, INITIAL_STATE)

  useEffect(() => {
    dispatchDraft({ action: 'sync', cardFilters: cardFilters || [] })
  }, [cardFilters])

  const onApply = () => {
    const cardFiltersNew = cleanGalleryFilters(cardFiltersDraft) || []
    const hasChanged = JSON.stringify(cardFiltersNew) !== JSON.stringify(cardFilters)
    dispatch(galleryActions.setCardFilters(cardFiltersNew))
    if (hasChanged) requestAnimationFrame(() => scrollToAnchor({ id: 'gallery', ifNearTop: true, isSmooth: true }))
    dispatchDraft({ action: 'close' })
  }

  const saveEnabled = isEditMode && cardFiltersDraft.every((f) => isCardFilterType(f))

  useClickaway({
    ignore: 'card-filters',
    onClickAway: () => {
      if (saveEnabled) onApply()
      onClickClose?.()
    },
  })

  const showAdd = cardFiltersDraft.length < 8

  return (
    <div
      {...getClickawayIgnoreProps(['card-filters'])}
      className={cn(styles.container, { isEditMode, styles, variant })}
    >
      <h2 className={styles.heading}>
        <IconWithLabel iconBefore='filter' iconBeforeMargin='sm' iconBeforeSize='sm'>
          Card Filters
        </IconWithLabel>
        <FloatingHover
          content={
            <>
              <p>
                <strong>Card Filters</strong> adjust the card results displayed in the gallery.
              </p>
              <p>
                <strong>Note:</strong> Card filters are <strong>not the same</strong> as the main{' '}
                <strong>Filters</strong>. Main filters adjust the underlying deck data, while card filters adjust the
                card results.
              </p>
            </>
          }
          placement='right'
        >
          <ButtonRoot aria-label='Learn More' className={styles.infoIcon}>
            <Icon name='circleInfo' />
          </ButtonRoot>
        </FloatingHover>
        <span className={styles.lock}>
          <GalleryControlsLock slug='cardFilters' />
        </span>
        {onClickClose && <GalleryCardFiltersClose onClickClose={onClickClose} />}
      </h2>
      <div className={styles.content}>
        {isEditMode && cardFiltersDraft.length < 1 && <p className={styles.emptyMessage}>No card filters set.</p>}
        <ul className={styles.list}>
          {cardFiltersDraft.map((cardFilter, index) => (
            <li key={index}>
              {cardFilter.isEditing ? (
                <GalleryCardFiltersForm
                  cardFilter={cardFilter}
                  onDelete={() => dispatchDraft({ action: 'delete', index })}
                  onSubmit={(filter) => dispatchDraft({ action: 'submitFilter', filter, index })}
                  onUpdate={(filter) => dispatchDraft({ action: 'updateFilter', filter, index })}
                />
              ) : (
                <GalleryCardFiltersItem
                  cardFilter={cardFilter}
                  onDelete={() => dispatchDraft({ action: 'delete', index })}
                  onEdit={() => dispatchDraft({ action: 'edit', index })}
                />
              )}
            </li>
          ))}
          {showAdd && (
            <li>
              <InputSelect
                onSelect={(slug) => dispatchDraft({ action: 'add', slug })}
                optionGroups={optionGroups}
                width='maxcontent'
              >
                <ButtonDefault as='div' {...getClickawayIgnoreProps(['filter-form'])}>
                  <IconWithLabel iconAfter='plus'>Add</IconWithLabel>
                </ButtonDefault>
              </InputSelect>
            </li>
          )}
        </ul>
        {isEditMode && (
          <div className={styles.actions}>
            <ButtonText
              hover='warning'
              onClick={() => dispatchDraft({ action: 'close' })}
              {...getClickawayIgnoreProps(['filter-form'])}
            >
              Cancel
            </ButtonText>
            <ButtonDefault
              disabled={!saveEnabled}
              onClick={onApply}
              {...(saveEnabled ? getClickawayIgnoreProps(['filter-form']) : {})}
            >
              Apply Changes
            </ButtonDefault>
          </div>
        )}
      </div>
    </div>
  )
}
